/**
 * Session headers on API responses (brief: "Response headers the
 * dashboard should read"). Durations are relative, in whole seconds, so
 * the dashboard never compares its clock with the server's: each one
 * becomes `now + seconds * 1000 - margin` on receipt.
 *
 * Both HTTP clients call `recordSessionHeaders` on every response. It
 * stores what the idle feature needs and reports whether the response
 * was an authenticated one (the sign the user is signed in — what the
 * deprecated `Authorization-Expires` used to signal).
 */
import { HEADER_MARGIN_MS, SESSION_HEADERS } from './constants';
import { writeIdleTimeoutMs, writeTokenDeadlineAt } from './idleStore';

/** Whole seconds from a header value; `null` for anything else. */
export const parseHeaderSeconds = value => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (trimmed === '') return null;
  const seconds = Number(trimmed);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
};

/** Epoch ms deadline for a relative header, shortened by the margin. */
export const deadlineFromSeconds = (now, seconds, marginMs = HEADER_MARGIN_MS) =>
  Math.max(now, now + seconds * 1000 - marginMs);

/**
 * @param {Headers} headers  the fetch Response headers
 * @param {number} [now]
 * @returns {{ authenticated: boolean, tokenDeadlineAt: number|null, idleTimeoutMs: number|null }}
 */
export const recordSessionHeaders = (headers, now = Date.now()) => {
  const get = name => (headers && typeof headers.get === 'function' ? headers.get(name) : null);

  const tokenSeconds = parseHeaderSeconds(get(SESSION_HEADERS.tokenExpiresIn));
  const tokenDeadlineAt = tokenSeconds === null ? null : deadlineFromSeconds(now, tokenSeconds);
  if (tokenDeadlineAt !== null) writeTokenDeadlineAt(tokenDeadlineAt);

  const idleSeconds = parseHeaderSeconds(get(SESSION_HEADERS.idleTimeout));
  const idleTimeoutMs = idleSeconds === null || idleSeconds === 0 ? null : idleSeconds * 1000;
  if (idleTimeoutMs !== null) writeIdleTimeoutMs(idleTimeoutMs);

  const authenticated = tokenDeadlineAt !== null || get(SESSION_HEADERS.legacyExpires) !== null;

  return { authenticated, tokenDeadlineAt, idleTimeoutMs };
};
